import { TOOLTIP } from '../../../../constants/tooltips'
import { InputWidth } from '../../../../types/aoraki/input-width-types'
import { WellStratumFieldsEdit } from '../../../../types/form-types'
import { SetValue, ValidationErrors } from '../../../hooks/use-edit-form'
import LookupField from '../../wells/lookup-field'
import FractionCardEditWrapper from './fraction-card-edit-wrapper'
import { preventChangeOnScroll } from '../../../services/form-utils'
import { Divider, FormField, Heading, RecordContent, TextInput } from '@msaf/core-react'

interface WellStratumEditFormProps {
  isLoading: boolean
  fields: Partial<WellStratumFieldsEdit>
  setValue: SetValue<WellStratumFieldsEdit>
  validationErrors?: ValidationErrors<WellStratumFieldsEdit>
  isNewRecord?: boolean
  isExistingOrDecommissioned?: boolean
  councilId?: number
}

export function WellStratumEditForm({
  isLoading,
  fields,
  setValue,
  validationErrors = {},
  isNewRecord,
  isExistingOrDecommissioned,
  councilId,
}: WellStratumEditFormProps) {
  return (
    <RecordContent>
      <Heading level={2}>Stratum</Heading>
      <Heading isSkeleton={isLoading} level={3}>
        Identifiers
      </Heading>
      <FormField
        isSkeleton={isLoading}
        htmlFor='stratumBottom'
        label='Bottom Level of Stratum(m)'
        isRequired={isExistingOrDecommissioned}
        validationMessages={validationErrors?.stratumBottom}
      >
        <TextInput
          fieldSize={InputWidth.large}
          id='bottomLevel'
          isSkeleton={isLoading}
          type='number'
          onWheel={preventChangeOnScroll}
          value={fields.stratumBottom ?? ''}
          setValue={(value) => setValue('stratumBottom', value)}
          isInvalid={!!validationErrors?.stratumBottom}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.drillerStratumDescription}
        isSkeleton={isLoading}
        htmlFor='description'
        label='Driller&#39;s description'
        toolTipMessage={TOOLTIP.drillerStratumDescription}
      >
        <TextInput
          isSkeleton={isLoading}
          id='description'
          value={fields.drillerStratumDescription ?? ''}
          setValue={(value) => setValue('drillerStratumDescription', value)}
          isInvalid={!!validationErrors?.drillerStratumDescription}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.geologicalFormation}
        isSkeleton={isLoading}
        htmlFor='geologicalFormationDescription'
        label='Geological formation description'
      >
        {councilId != null ? (
          <LookupField
            isSkeleton={isLoading}
            labelledBy='lithologyGeologicalFormation'
            fieldId='lithologyGeologicalFormation'
            value={fields.geologicalFormation ?? ''}
            setValue={({ value }) => setValue('geologicalFormation', value)}
            isInvalid={!!validationErrors?.geologicalFormation}
            fieldFilters={{ councilId }}
          />
        ) : null}
      </FormField>
      <FormField
        validationMessages={validationErrors?.moistureCondition}
        isSkeleton={isLoading}
        labelId='moistureCondition'
        label='Moisture Condition'
      >
        <LookupField
          isSkeleton={isLoading}
          labelledBy='moistureCondition'
          fieldId='lithologyMoistureCondition'
          value={fields.moistureCondition ?? ''}
          setValue={({ value }) => setValue('moistureCondition', value)}
          isInvalid={!!validationErrors?.moistureCondition}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.majorColour}
        isSkeleton={isLoading}
        labelId='majorColour'
        label='Primary colour'
      >
        <LookupField
          isSkeleton={isLoading}
          labelledBy='majorColour'
          fieldId='majorColour'
          value={fields.majorColour ?? ''}
          setValue={({ value }) => setValue('majorColour', value)}
          isInvalid={!!validationErrors?.majorColour}
        />
      </FormField>

      <FormField isSkeleton={isLoading} labelId='secondaryColour' label='Secondary colour'>
        <LookupField
          isSkeleton={isLoading}
          labelledBy='secondaryColour'
          fieldId='secondaryColour'
          value={fields.secondaryColour ?? ''}
          setValue={({ value }) => setValue('secondaryColour', value)}
          validationMessages={validationErrors?.secondaryColour}
          isInvalid={!!validationErrors?.secondaryColour}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.relativeDensity}
        isSkeleton={isLoading}
        labelId='relativeDensity'
        label='Relative density'
      >
        <LookupField
          isSkeleton={isLoading}
          labelledBy='relativeDensity'
          fieldId='lithologyRelativeDensity'
          value={fields.relativeDensity ?? ''}
          setValue={({ value }) => setValue('relativeDensity', value)}
          isInvalid={!!validationErrors?.relativeDensity}
        />
      </FormField>

      <Divider />

      <FractionCardEditWrapper
        isLoading={isLoading}
        stratum={fields}
        setValue={setValue}
        validationErrors={validationErrors}
        isNewRecord={isNewRecord}
        isExistingOrDecommissioned={isExistingOrDecommissioned}
      />
    </RecordContent>
  )
}
